import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Upload, Trash2, FileText, ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ClientAvatar from "./ClientAvatar";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isImage(ct = "") {
  return /image\//i.test(ct);
}

/**
 * Dialog para gerenciar foto + anexos do cliente.
 * - Upload de avatar
 * - Upload de anexos (foto da raiz, receita, etc) com label opcional
 * - Remover anexo
 */
export default function ClientGalleryDialog({ open, onOpenChange, client, services = [], onUpdated }) {
  const { apiCall } = useAuth();
  const [working, setWorking] = useState(false);
  const [uploadLabel, setUploadLabel] = useState("");
  const [attachments, setAttachments] = useState(client?.attachments || []);
  const [avatarUrl, setAvatarUrl] = useState(client?.avatar_url || null);
  const avatarInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setAttachments(client?.attachments || []);
    setAvatarUrl(client?.avatar_url || null);
  }, [client]);

  if (!client) return null;

  const authHeaders = () => {
    const token = localStorage.getItem("access_token");
    return { Authorization: `Bearer ${token}` };
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setWorking(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/clients/${client.client_id}/avatar`, fd, {
        headers: authHeaders(),
      });
      const newUrl = `${res.data.avatar_url}?t=${Date.now()}`;
      setAvatarUrl(newUrl);
      toast.success("Foto atualizada");
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar foto");
    } finally {
      setWorking(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!avatarUrl) return;
    if (!window.confirm("Remover foto do cliente?")) return;
    setWorking(true);
    try {
      await apiCall("delete", `/clients/${client.client_id}/avatar`);
      setAvatarUrl(null);
      toast.success("Foto removida");
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    } finally {
      setWorking(false);
    }
  };

  const handleAttachmentChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setWorking(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (uploadLabel.trim()) fd.append("label", uploadLabel.trim());
      const res = await axios.post(`${API}/clients/${client.client_id}/attachments`, fd, {
        headers: authHeaders(),
      });
      setAttachments((prev) => [...prev, res.data]);
      setUploadLabel("");
      toast.success("Anexo adicionado");
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar arquivo");
    } finally {
      setWorking(false);
    }
  };

  const handleDeleteAttachment = async (att) => {
    if (!window.confirm(`Remover "${att.filename}"?`)) return;
    setWorking(true);
    try {
      await apiCall("delete", `/clients/${client.client_id}/attachments/${att.attachment_id}`);
      setAttachments((prev) => prev.filter((a) => a.attachment_id !== att.attachment_id));
      toast.success("Anexo removido");
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    } finally {
      setWorking(false);
    }
  };

  // Renderiza usando o estado local atualizado
  const liveClient = { ...client, avatar_url: avatarUrl };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="client-gallery-dialog">
        <DialogHeader>
          <DialogTitle className="font-light tracking-tight text-2xl">
            Galeria — {client.name}
          </DialogTitle>
        </DialogHeader>

        {/* AVATAR */}
        <div className="flex items-center gap-4 mt-4 p-4 rounded-md border border-border bg-secondary/30">
          <ClientAvatar client={liveClient} services={services} size={88} />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
              Foto do cliente
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              Se não houver foto, mostramos um símbolo do serviço mais usado pelo cliente.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                size="sm"
                onClick={() => avatarInputRef.current?.click()}
                disabled={working}
                data-testid="upload-avatar-btn"
              >
                <Camera className="h-4 w-4 mr-1" strokeWidth={1.5} />
                {avatarUrl ? "Trocar foto" : "Enviar foto"}
              </Button>
              {avatarUrl && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleRemoveAvatar}
                  disabled={working}
                  data-testid="remove-avatar-btn"
                >
                  <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  Remover
                </Button>
              )}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
                data-testid="avatar-file-input"
              />
            </div>
          </div>
        </div>

        {/* ATTACHMENTS */}
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            Anexos ({attachments.length})
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Adicione fotos do antes/depois, cor da raiz, receita, ou qualquer documento do cliente.
          </p>

          <div className="flex gap-2 items-end mb-3">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Descrição (opcional)
              </label>
              <Input
                placeholder="Ex: cor da raiz, receita do dermato…"
                value={uploadLabel}
                onChange={(e) => setUploadLabel(e.target.value)}
                data-testid="attachment-label-input"
              />
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={working}
              data-testid="upload-attachment-btn"
            >
              <Upload className="h-4 w-4 mr-1" strokeWidth={1.5} />
              Anexar
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleAttachmentChange}
              data-testid="attachment-file-input"
            />
          </div>

          {attachments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhum anexo ainda.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {attachments.map((att) => (
                <div
                  key={att.attachment_id}
                  className="border border-border rounded-md overflow-hidden group relative bg-secondary/20"
                  data-testid={`attachment-card-${att.attachment_id}`}
                >
                  {isImage(att.content_type) ? (
                    <a href={att.url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={att.url}
                        alt={att.filename}
                        className="w-full h-24 object-cover"
                        loading="lazy"
                      />
                    </a>
                  ) : (
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center justify-center h-24 text-muted-foreground"
                    >
                      <FileText className="h-6 w-6 mb-1" strokeWidth={1.5} />
                      <span className="text-[10px] text-center px-1 truncate w-full">
                        {att.filename}
                      </span>
                    </a>
                  )}
                  <div className="p-1.5 flex items-center justify-between gap-1">
                    <div className="text-[10px] truncate flex-1" title={att.label || att.filename}>
                      {att.label || att.filename}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteAttachment(att)}
                      className="text-destructive opacity-60 hover:opacity-100"
                      title="Remover"
                      data-testid={`attachment-delete-${att.attachment_id}`}
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
